import React, { useEffect, useRef, useState } from 'react';
import { Send, Loader2, Info } from 'lucide-react';
import { isActiveAccount, getAccountStatus, LIFECYCLE_LABELS } from '../../utils/accountLifecycle';

const ChatInput = ({ onSend, disabled = false, optedOut = false, lead = null }) => {
  const [message, setMessage] = useState('');
  const inputRef = useRef(null);

  // Listen for voicemail "Reply" CTAs dispatched by MessageBubble. Prefills
  // the composer with the tap-source's text and focuses the input. Focusing
  // on mobile auto-raises the keyboard; the composer stays visible because
  // ChatInput is the bottom of a flex column (ChatWindow) — no extra layout
  // work needed.
  useEffect(() => {
    const onPrefill = (ev) => {
      const text = ev?.detail?.text || '';
      if (!text) return;
      setMessage(text);
      // Defer to next tick so the input isn't stolen by a competing focus.
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        try { el.setSelectionRange(el.value.length, el.value.length); } catch (_) {}
      });
    };
    window.addEventListener('kro:voicemail-reply', onPrefill);
    return () => window.removeEventListener('kro:voicemail-reply', onPrefill);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && !disabled && !optedOut) {
      onSend(message);
      setMessage('');
    }
  };

  const isBlocked = disabled || optedOut;
  // Phase E: show a small read-only hint when the lead is paused/closed.
  // Manual sending is fully allowed — the banner is purely informational.
  const showLifecycleHint = !!lead && !isActiveAccount(lead);
  const lifecycleStatus = showLifecycleHint ? getAccountStatus(lead) : null;

  return (
    <div className="border-t border-gray-200 p-4 bg-white relative z-10">
      {showLifecycleHint && (
        <div
          className="mb-2 flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs"
          data-testid="chat-input-lifecycle-hint"
        >
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Sending to a <strong>{LIFECYCLE_LABELS[lifecycleStatus]}</strong> account.
            Automated playbooks are suppressed — this manual message will still send.
          </span>
        </div>
      )}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={optedOut ? 'Messaging is disabled — contact opted out' : 'Type a message...'}
          disabled={isBlocked}
          data-testid="chat-input-textbox"
          className={`
            flex-1 px-4 py-2 border rounded-lg focus:outline-none transition-colors
            ${optedOut
              ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed placeholder:text-gray-400 italic'
              : 'border-gray-300 focus:ring-2 focus:ring-[#0A2647] disabled:bg-gray-50 disabled:text-gray-500'
            }
          `}
        />
        <button
          type="submit"
          disabled={isBlocked || !message.trim()}
          className="px-4 py-2 bg-[#0A2647] text-white rounded-lg hover:bg-[#0A2647]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {disabled && !optedOut
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <Send className="h-4 w-4" />
          }
          <span className="hidden sm:inline">
            {disabled && !optedOut ? 'Sending...' : 'Send'}
          </span>
        </button>
      </form>
    </div>
  );
};

export default ChatInput;
